import {
  Box,
  Collapsible,
  HStack,
  Heading,
  IconButton,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import SearchInput from "@pathwright/ui/src/components/form/form-text-input/SearchInput"
import useDebouncedValue from "@pathwright/ui/src/components/hooks/useDebouncedValue"
import useIntersection from "@pathwright/ui/src/components/hooks/useIntersection"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import { UsePaginatorReturn } from "@pathwright/utils"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import {
  COMPLETION_FILTERS,
  CompletionFilterKey
} from "../context/completion-filters"
import { useReviewContext } from "../state/ReviewContextProvider"
import InboxNavigatorItem from "./InboxNavigatorItem"
import useInboxItems, { InboxGroupNode } from "./useInboxItems"

const ItemsContainer = styled(VStack)`
  width: 100%;
  background-color: rgba(255, 255, 255, 1);
  border-radius: 16px;
  max-height: calc(var(--vh) - 200px);

  > nav {
    display: flex;
    align-items: center;
    li[role="tab"] {
      flex-grow: 0;
      margin: 0.2em;
    }
  }

  .InboxNavigator__items {
    overflow-y: auto;
    flex-grow: 1;
    list-style: none;
    padding: 0;

    > li {
      width: 100%;

      .InboxNavigatorItem {
        padding: var(--chakra-spacing-3) var(--chakra-spacing-4);
      }
    }

    > li:last-child .InboxNavigatorItem {
      border-bottom: none;
    }
  }
`

const LoadingMore = styled.div`
  height: 50px;
  width: 100%;
  position: relative;
`

// Paginate items on scroll.
const usePaginate = ({
  loading,
  loadMore,
  hasMore,
  loadingMore
}: Pick<UsePaginatorReturn, "loadMore" | "hasMore" | "loadingMore"> & {
  loading: boolean
}) => {
  const [scrollNode, setScrollNode] = useState()
  const { setIntersectionNode, hasIntersected } = useIntersection({
    root: scrollNode
  })

  useEffect(() => {
    if (hasIntersected && !loading && !loadingMore && hasMore) loadMore()
  }, [hasIntersected])

  return [setScrollNode, setIntersectionNode]
}

const InboxNavigator = ({
  onSelect
}: {
  onSelect?: (item: InboxGroupNode) => void
}) => {
  const [search, debouncedSearch, debouncingSearch, handleDebouncedSearch] =
    useDebouncedValue(300, null)
  const { context } = useReviewContext()
  const { items, loading, loadMore, hasMore, loadingMore } = useInboxItems({
    search: debouncedSearch
  })
  const searching = debouncingSearch || (loading && search)
  const { t } = useTranslate()
  const [setScrollNode, setIntersectionNode] = usePaginate({
    loading,
    loadMore,
    hasMore,
    loadingMore
  })
  const disclosure = useDisclosure()

  // Clear search when opening/closing the search input.
  useEffect(() => {
    handleDebouncedSearch(null)
  }, [disclosure.open])

  const history = useHistory()

  const handleSelect =
    (item: InboxGroupNode, filter: CompletionFilterKey) => () => {
      // Preserving default filter and order params if they are set.
      let params = {
        ...context?.queryParams,
        filter,
        order: "asc" as "asc",
        ui_selected_parent: `${item.id!}`
      }

      switch (item.type) {
        case "group":
          params.group_id = item.id
          break
        case "path":
          params.cohort_id = item.id
          break
      }

      // Remove null/undefined values.
      params = Object.entries(params).reduce((obj, [key, value]) => {
        if (value != null) {
          obj[key] = value
        }
        return obj
      }, {} as Record<string, unknown>) as typeof params

      const location = {
        pathname: "/inbox/",
        search: `?${new URLSearchParams(params as {}).toString()}`
      }
      history.push(location)

      if (typeof onSelect === "function") {
        onSelect(item)
      }
    }

  return (
    <ItemsContainer className="InboxNavigator">
      <HStack as="header" px={4} pt={4} w="100%">
        <HStack flexGrow={1}>
          <Pathicon icon="inbox-fill" />
          <Heading size="2xl" m={0}>
            Inbox
          </Heading>
        </HStack>
        <IconButton
          variant={disclosure.open ? "subtle" : "ghost"}
          onClick={disclosure.onToggle}
          aria-expanded={disclosure.open}
          rounded="full"
          size="xs"
        >
          <Pathicon icon="search" />
        </IconButton>
      </HStack>
      {disclosure.open && (
        <Collapsible.Root
          open={disclosure.open}
          style={{
            width: "100%",
            // Note: forcing overflow visible to ensure search input isn't clipped.
            overflow: "visible"
          }}
        >
          <Collapsible.Content px={4} w="100%">
            <SearchInput
              placeholder="Search..."
              onChange={handleDebouncedSearch}
              onClear={() => handleDebouncedSearch(null)}
              searching={searching}
              value={search}
            />
          </Collapsible.Content>
        </Collapsible.Root>
      )}
      {loading && !items.length && !searching ? (
        <LoadingCircle center />
      ) : !items.length ? (
        <BlankSlate icons={false} heading={t("Nothing to review")} />
      ) : (
        <VStack
          as="ul"
          className="InboxNavigator__items"
          ref={setScrollNode as React.Ref<HTMLDivElement>}
          w="100%"
          gap={0}
        >
          {items.map((item, i) => (
            // Paginate after second to last item comes into view.
            <Box
              as="li"
              key={`item-${i}`}
              ref={i === items.length - 2 ? setIntersectionNode : undefined}
            >
              <InboxNavigatorItem
                key={`${item.id}${item.type}`}
                item={item}
                onSelect={handleSelect(item, COMPLETION_FILTERS.pending.key)}
                // onSelectPending={handleSelect(
                //   item,
                //   COMPLETION_FILTERS.pending.key
                // )}
              />
            </Box>
          ))}
          {hasMore && (
            <LoadingMore>
              <LoadingCircle center />
            </LoadingMore>
          )}
        </VStack>
      )}
    </ItemsContainer>
  )
}

InboxNavigator.displayName = "InboxNavigator"

export default InboxNavigator
